/**
 * lexicalEdit — the native Lexical EDITING core (the engine half of the RN port).
 * Runs the real Lexical engine headless (no DOM), so RN drives it via a plain
 * TextInput instead of @lexical/react's contentEditable.
 *
 * The crux is mapping the TextInput's plain-text char offsets ↔ Lexical points.
 * We build BOTH the plain text and a per-offset point map in ONE walk, so the
 * diff is self-consistent and never depends on Lexical's getTextContent newline
 * convention. A single-range diff (common prefix/suffix) + selection.insertText
 * then edits in place, preserving the inline formatting outside the changed run.
 */
import { createHeadlessEditor } from "@lexical/headless";
import {
  $getRoot,
  $isTextNode,
  $isElementNode,
  $createRangeSelection,
  $setSelection,
  type LexicalEditor,
  type LexicalNode,
  type ElementNode,
} from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";

// The SAFE nodes (no DOM decorators like YouTube/Autocomplete) — enough to parse
// the common web content; unknown nodes fall back via parse try/catch.
export const NATIVE_LEXICAL_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  LinkNode,
];

export function createNativeEditor(initialState?: string): LexicalEditor {
  const editor = createHeadlessEditor({
    namespace: "native-lexical",
    nodes: NATIVE_LEXICAL_NODES,
    onError: () => {
      /* keep going on a node we don't register */
    },
  });
  if (initialState) {
    try {
      editor.setEditorState(editor.parseEditorState(initialState));
    } catch {
      /* leave the empty editor */
    }
  }
  return editor;
}

export function serializeEditor(editor: LexicalEditor): string {
  return JSON.stringify(editor.getEditorState().toJSON());
}

type Pt = { key: string; offset: number; type: "text" | "element" };

/** Text leaves in document order (descends into links etc.). */
function textLeaves(node: LexicalNode, out: LexicalNode[]): void {
  if ($isTextNode(node)) {
    out.push(node);
  } else if ($isElementNode(node)) {
    for (const child of node.getChildren()) textLeaves(child, out);
  }
}

/** A point at the very end of a block (after its last char). */
function endOfBlock(block: LexicalNode): Pt {
  const leaves: LexicalNode[] = [];
  textLeaves(block, leaves);
  const last = leaves[leaves.length - 1];
  if (last && $isTextNode(last)) {
    return {
      key: last.getKey(),
      offset: last.getTextContent().length,
      type: "text",
    };
  }
  const el = block as ElementNode;
  return { key: el.getKey(), offset: el.getChildrenSize(), type: "element" };
}

interface Model {
  text: string;
  /** points[k] = the Lexical point BEFORE plain-text char k; length = text.length+1. */
  points: Pt[];
}

/** Build the plain text + per-char point map (blocks joined by a single "\n"). */
export function buildModel(editor: LexicalEditor): Model {
  let text = "";
  const points: Pt[] = [];
  editor.getEditorState().read(() => {
    const blocks = $getRoot().getChildren();
    blocks.forEach((block, bi) => {
      if (bi > 0) {
        // separator "\n" — its preceding point is the end of the previous block.
        points.push(endOfBlock(blocks[bi - 1]!));
        text += "\n";
      }
      const leaves: LexicalNode[] = [];
      textLeaves(block, leaves);
      for (const leaf of leaves) {
        const t = leaf.getTextContent();
        for (let i = 0; i < t.length; i++) {
          points.push({ key: leaf.getKey(), offset: i, type: "text" });
          text += t[i];
        }
      }
    });
    // trailing end point
    points.push(
      blocks.length
        ? endOfBlock(blocks[blocks.length - 1]!)
        : { key: $getRoot().getKey(), offset: 0, type: "element" },
    );
  });
  return { text, points };
}

/** Apply a new full plain-text value, editing in place (formatting preserved). */
export function applyPlainTextEdit(
  editor: LexicalEditor,
  newText: string,
): void {
  const { text: oldText, points } = buildModel(editor);
  if (newText === oldText) return;

  // single-range diff: common prefix + common suffix
  let p = 0;
  const max = Math.min(oldText.length, newText.length);
  while (p < max && oldText[p] === newText[p]) p++;
  let s = 0;
  while (
    s < max - p &&
    oldText[oldText.length - 1 - s] === newText[newText.length - 1 - s]
  )
    s++;
  const startOff = p;
  const endOff = oldText.length - s;
  const inserted = newText.slice(p, newText.length - s);

  const a = points[startOff]!;
  const b = points[endOff]!;
  editor.update(
    () => {
      const sel = $createRangeSelection();
      sel.anchor.set(a.key, a.offset, a.type);
      sel.focus.set(b.key, b.offset, b.type);
      $setSelection(sel);
      sel.insertText(inserted);
    },
    { discrete: true },
  );
}

/** Toggle a text format (bold/italic/…) over a plain-text offset range. */
export function formatRange(
  editor: LexicalEditor,
  startOff: number,
  endOff: number,
  format: "bold" | "italic" | "underline" | "strikethrough" | "code",
): void {
  if (endOff <= startOff) return;
  const { points } = buildModel(editor);
  const a = points[startOff];
  const b = points[endOff];
  if (!a || !b) return;
  editor.update(
    () => {
      const sel = $createRangeSelection();
      sel.anchor.set(a.key, a.offset, a.type);
      sel.focus.set(b.key, b.offset, b.type);
      $setSelection(sel);
      sel.formatText(format);
    },
    { discrete: true },
  );
}
